/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import type Router from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import type ReportingViews from 'embercom/services/reporting-views';
import { type ReportingView } from 'embercom/services/reporting-views';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import { didCancel } from 'ember-concurrency';
import type CustomReportsService from 'embercom/services/custom-reports-service';
import { ViewId } from 'embercom/services/reporting-views';

export default class ViewRoute extends Route {
  @service declare appService: AppService;
  @service declare reportingViews: ReportingViews;
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare intercomEventService: any;
  @service declare permissionsService: any;
  @service declare customReportsService: CustomReportsService;

  titleToken = '...';

  async model(params: any, transition: any) {
    let viewIds = this.reportingViews.reportTabs.map((view) => view.id);

    if (!viewIds.includes(params.reporting_view_id)) {
      return this.router.transitionTo('apps.app.reports.views.view', ViewId.SHARED_REPORT);
    }

    let isSwitchingTabs = JSON.parse(transition.to.queryParams.isSwitchingTabs ?? 'false');
    if (this.reportingViews.canViewCustomReports && !isSwitchingTabs) {
      // do not reload when user is switching tabs
      try {
        await taskFor(this.reportingViews.refreshReports).perform();
      } catch (e) {
        if (!didCancel(e)) {
          throw e;
        }
      }
    }

    let view: ReportingView;

    if (isSwitchingTabs) {
      view = this.reportingViews.findView(params.reporting_view_id)!;
    } else {
      // if user has no reports in shared with me default to intercom report
      view = this.getView(params.reporting_view_id)!;
    }
    this.titleToken = this.intl.t(view.name);
    return view;
  }

  private getView(viewId: string) {
    let view = this.reportingViews.findView(viewId);
    if (viewId === ViewId.SHARED_REPORT && view && view.reports.length === 0) {
      return this.reportingViews.findView(ViewId.INTERCOM_REPORTS);
    }
    return view;
  }

  @action
  async newReportPermissions() {
    if (!this.customReportsService.canChangeCustomReports) {
      this.customReportsService.loadChangePermissionModal();
    } else {
      this.router.transitionTo('apps.app.reports.custom-reports.report.new');
    }
  }
}
